import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { formatDate } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  FileText, 
  Download, 
  Trash2, 
  Eye, 
  Clock, 
  MapPin, 
  User, 
  Calendar,
  Shield,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PrivacyCenter = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [myData, setMyData] = useState(null);
  const [consents, setConsents] = useState([]);
  
  // Data Export
  const [exporting, setExporting] = useState(false);
  
  // Data Deletion
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchPrivacyData();
  }, []);

  const fetchPrivacyData = async () => {
    setLoading(true);
    try {
      const [dataRes, consentsRes] = await Promise.all([
        axios.get(`${API}/api/privacy/my-data`, { headers }),
        axios.get(`${API}/api/privacy/consents`, { headers })
      ]);
      
      setMyData(dataRes.data);
      setConsents(consentsRes.data);
    } catch (error) {
      console.error('Error fetching privacy data:', error);
      toast.error('Failed to load privacy data');
    } finally {
      setLoading(false);
    }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await axios.post(`${API}/api/privacy/export-data`, { format: 'json' }, { headers });
      
      // Create downloadable file
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const requestDeletion = async () => {
    if (!confirmDelete) {
      toast.error('Please confirm you understand the consequences');
      return;
    }
    
    setDeleting(true);
    try {
      await axios.post(`${API}/api/privacy/delete-request`, {
        password: deletePassword,
        reason: deleteReason
      }, { headers });
      
      toast.success('Deletion request submitted. Your data will be deleted within 30 days.');
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit deletion request');
    } finally {
      setDeleting(false);
    }
  };

  const recordConsent = async (type, accepted) => {
    try {
      await axios.post(`${API}/api/privacy/consent`, {
        consent_type: type,
        accepted,
        timestamp: new Date().toISOString()
      }, { headers });
      
      toast.success(`Consent ${accepted ? 'given' : 'withdrawn'}`);
      fetchPrivacyData();
    } catch (error) {
      toast.error('Failed to update consent');
    }
  };

  const getConsentStatus = (type) => {
    const consent = consents.find(c => c.consent_type === type);
    return consent?.accepted || false;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const dataCounts = myData ? {
    timeEntries: myData.time_entries?.length || 0,
    rosterEntries: myData.roster_entries?.length || 0,
    notifications: myData.notifications?.length || 0,
    leaveRequests: myData.leave_requests?.length || 0,
    loginHistory: myData.login_history?.length || 0
  } : {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 p-4 md:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-lg">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Privacy Center</h1>
            <p className="text-slate-500">Manage your data and privacy preferences</p>
          </div>
        </div>

        {/* Data Overview */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-indigo-600" />
              Your Data Overview
            </CardTitle>
            <CardDescription>
              Summary of data we store about you (GDPR Article 15)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <Clock className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-700">{dataCounts.timeEntries}</p>
                <p className="text-sm text-blue-600">Time Entries</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg text-center">
                <Calendar className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-purple-700">{dataCounts.rosterEntries}</p>
                <p className="text-sm text-purple-600">Roster Entries</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg text-center">
                <FileText className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-amber-700">{dataCounts.leaveRequests}</p>
                <p className="text-sm text-amber-600">Leave Requests</p>
              </div>
            </div>

            {/* Profile Data */}
            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <User className="w-4 h-4" />
                Profile Information
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-slate-500">Name:</span>
                  <span className="ml-2 font-medium">{myData?.profile?.name}</span>
                </div>
                <div>
                  <span className="text-slate-500">Email:</span>
                  <span className="ml-2 font-medium">{myData?.profile?.email}</span>
                </div>
                <div>
                  <span className="text-slate-500">Role:</span>
                  <span className="ml-2 font-medium capitalize">{myData?.profile?.role?.replace('_', ' ')}</span>
                </div>
                <div>
                  <span className="text-slate-500">Account Created:</span>
                  <span className="ml-2 font-medium">
                    {myData?.profile?.created_at ? formatDate(myData.profile.created_at) : 'N/A'}
                  </span>
                </div>
              </div>
              {myData?.facial_recognition && (
                <div className="mt-3 p-2 bg-amber-100 rounded text-amber-800 text-sm">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Facial recognition data is registered for your account
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Data Export */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-indigo-600" />
              Export Your Data
            </CardTitle>
            <CardDescription>
              Download a copy of all your data (GDPR Article 20 - Data Portability)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">Download all your data</p>
                <p className="text-sm text-slate-500">
                  Includes profile, time entries, roster, leave requests, and login history
                </p>
              </div>
              <Button 
                onClick={exportData} 
                disabled={exporting}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {exporting ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Exporting...
                  </span>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Export as JSON
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Consent Management */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-indigo-600" />
              Consent Preferences
            </CardTitle>
            <CardDescription>
              Manage what you consent to
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">Privacy Policy</p>
                <p className="text-sm text-slate-500">Agreement to our data processing practices</p>
              </div>
              <Badge className={getConsentStatus('privacy_policy') ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}>
                {getConsentStatus('privacy_policy') ? 'Accepted' : 'Not Accepted'}
              </Badge>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">Marketing Communications</p>
                <p className="text-sm text-slate-500">Receive product updates and news</p>
              </div>
              <Button
                variant={getConsentStatus('marketing') ? 'default' : 'outline'}
                size="sm"
                onClick={() => recordConsent('marketing', !getConsentStatus('marketing'))}
              >
                {getConsentStatus('marketing') ? 'Opted In' : 'Opt In'}
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">Location Data</p>
                <p className="text-sm text-slate-500">Allow collection of location for clock-in verification</p>
              </div>
              <Badge className={getConsentStatus('location_data') ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}>
                {getConsentStatus('location_data') ? 'Allowed' : 'Not Allowed'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Delete Account */}
        <Card className="border-0 shadow-lg border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Your Data
            </CardTitle>
            <CardDescription>
              Request deletion of all your data (GDPR Article 17 - Right to be Forgotten)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">This action is irreversible</p>
                  <p className="text-sm text-red-700 mt-1">
                    Once processed, all your data including time entries, roster history, and account information will be permanently deleted. This typically takes up to 30 days.
                  </p>
                </div>
              </div>
              
              <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogTrigger asChild>
                  <Button variant="destructive" className="mt-4">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Request Data Deletion
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="text-red-600">Delete All Your Data</DialogTitle>
                    <DialogDescription>
                      This will permanently delete your account and all associated data
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Confirm Your Password</Label>
                      <Input
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        placeholder="Enter your password"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Reason for leaving (optional)</Label>
                      <Input
                        type="text"
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        placeholder="Help us improve"
                      />
                    </div>

                    <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg">
                      <Checkbox
                        id="confirm-delete"
                        checked={confirmDelete}
                        onCheckedChange={setConfirmDelete}
                      />
                      <label htmlFor="confirm-delete" className="text-sm text-red-700">
                        I understand that this action is permanent and all my data will be deleted within 30 days
                      </label>
                    </div>

                    <Button 
                      variant="destructive" 
                      onClick={requestDeletion}
                      disabled={!deletePassword || !confirmDelete || deleting}
                      className="w-full"
                    >
                      {deleting ? 'Submitting...' : 'Confirm Deletion Request'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyCenter;
